import { useGetProductVariants } from "@/hooks/seller/useGetProductVariants"
import { useRejectRfqAdminToSeller } from "@/hooks/seller/useRejectRfqAdminToSeller"
import { useQueryClient } from "@tanstack/react-query"
import { PromptModalCard } from "@/components/shared/PromptModalCard"
import { type RfqRejectionReason, type RejectRfqAdminToSellerPayload } from "@/lib/api/rfq.api"
import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "@tanstack/react-router"
import { useSellerProfile } from "@/hooks/seller/useSellerProfile"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { useCompanyAddresses } from "@/hooks/seller/useCompanyAddresses"
import { AddAddressForm } from "@/components/dashboard/shared/AddAddressForm"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { useGetRfqAdminToSellerById } from "@/hooks/admin/useGetRfqAdminToSellerById"
import { useCreateQuotation } from "@/hooks/seller/useCreateQuotation"
import { useCountryCurrencies } from "@/hooks/useCountryCurrencies"
import { DatePicker } from "@/components/ui/date-picker"
import {
  FileText,
  Loader2,
  Calendar,
  AlertCircle,
  Package,
  Plus,
  Check,
  X,
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  BadgeCheck, MapPin,
  Tag,
  Hash,
  Minus,
  Building2,
  MoreVertical,
  Trash2,
  AlertTriangle
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export const QUANTITY_UNITS = [
  { code: "kg", name: "Kilogram (kg)" },
  { code: "g", name: "Gram (g)" },
  { code: "ltr", name: "Liter (ltr)" },
  { code: "ml", name: "Milliliter (ml)" },
  { code: "pcs", name: "Pieces (pcs)" },
  { code: "qtl", name: "Quintal (qtl)" },
  { code: "ton", name: "Metric Ton (ton)" },
  { code: "box", name: "Box (box)" },
]

export const isIntegerUnit = (unit?: string | null): boolean => {
  if (!unit) return false
  const u = unit.toLowerCase().trim()
  return u === "pcs" || u === "box" || u.includes("piece") || u.includes("box")
}

export interface ChargeItem {
  description: string;
  unitPrice: number | string;
  discount: number | string;
  quantity: number | string;
  unit?: string;
  tax: number | string;
  taxType: string;
  productVariantId?: string;
}

const DEFAULT_CHARGE: ChargeItem = {
  description: "",
  unitPrice: "",
  discount: 0,
  quantity: "",
  unit: "",
  tax: 18,
  taxType: "GST",
  productVariantId: ""
};

export const MIN_TOTAL_PIECES = 1
export const MAX_TOTAL_PIECES = 10_000_000 // 10 Million

export const MIN_WEIGHT = 0.01
export const MAX_WEIGHT = 1_000_000 // 1 Million kg

export const MIN_VOLUME_WEIGHT = 0.01
export const MAX_VOLUME_WEIGHT = 1_000_000 // 1 Million kg

export const MAX_COMMODITY_LENGTH = 100

export function getCommodityError(val: string): string | null {
  if (!val.trim()) return null
  if (val.trim().length > MAX_COMMODITY_LENGTH) {
    return `Commodity cannot exceed ${MAX_COMMODITY_LENGTH} characters`
  }
  return null
}

export function getTotalPiecesError(val: string): string | null {
  if (val.trim() === "") return null
  const num = Number(val)
  if (isNaN(num) || num < MIN_TOTAL_PIECES) {
    return `Total pieces must be at least ${MIN_TOTAL_PIECES}`
  }
  if (num > MAX_TOTAL_PIECES) {
    return `Total pieces cannot exceed ${MAX_TOTAL_PIECES.toLocaleString()}`
  }
  return null
}

export function getWeightError(val: string): string | null {
  if (val.trim() === "") return null
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Weight must be greater than 0"
  }
  if (num > MAX_WEIGHT) {
    return `Weight cannot exceed ${MAX_WEIGHT.toLocaleString()} kg`
  }
  return null
}

export function getVolumeWeightError(val: string): string | null {
  if (val.trim() === "") return null
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Volume weight must be greater than 0"
  }
  if (num > MAX_VOLUME_WEIGHT) {
    return `Volume weight cannot exceed ${MAX_VOLUME_WEIGHT.toLocaleString()} kg`
  }
  return null
}

export function getChargeDescriptionError(val: string): string | null {
  if (val.length > 50) {
    return "Max 50 characters"
  }
  return null
}

export function getChargeUnitPriceError(val: string | number | ""): string | null {
  if (val === "") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Must be > 0"
  }
  if (num > 10_000_000) {
    return "Max 10,000,000"
  }
  return null
}

export function getChargeQuantityError(val: string | number | "" | null | undefined, unit?: string): string | null {
  if (val === "" || val === null || val === undefined) return null
  const str = String(val)
  if (isIntegerUnit(unit) && str.includes(".")) {
    return "Whole numbers only"
  }
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Must be > 0"
  }
  if (num >= 100_000) {
    return "Max 100,000"
  }
  return null
}

export function getChargeDiscountError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function getChargeTaxError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function getChargeUnitError(unit: string | undefined, quantity: string | number | "" | null | undefined): string | null {
  const hasQuantity = quantity !== "" && quantity !== undefined && quantity !== null
  if (hasQuantity && (!unit || !unit.trim())) {
    return "Select unit"
  }
  return null
}

export function isChargeRowCompleteAndValid(charge: ChargeItem): boolean {
  if (!charge.description.trim()) return false
  if (getChargeDescriptionError(charge.description) !== null) return false
  if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) return false
  if (getChargeUnitPriceError(charge.unitPrice) !== null) return false

  const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined && charge.quantity !== null
  if (hasQuantity) {
    if (isNaN(Number(charge.quantity))) return false
    if (getChargeQuantityError(charge.quantity, charge.unit) !== null) return false
    if (!charge.unit || !charge.unit.trim()) return false
  }

  if (getChargeDiscountError(charge.discount) !== null) return false
  if (getChargeTaxError(charge.tax) !== null) return false
  return true
}

export function RfqDetailsPage({ rfqId }: { rfqId: string }) {
  const navigate = useNavigate()
  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/rfqs" })
    }
  }

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetRfqAdminToSellerById(rfqId, !!rfqId)

  const selectedRfq = detailData?.adminToSellerRfq || null

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-primary/20 rounded-2xl shadow-sm w-full max-w-5xl mx-auto font-sans text-left animate-in fade-in duration-200">
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800"
          title="Back to RFQ Workspace"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching RFQ details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to RFQ Workspace"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">{(detailError as Error)?.message || "Failed to load RFQ details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedRfq) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to RFQ Workspace"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">RFQ details not found.</p>
        </div>
      </div>
    )
  }

  return <RfqDetailView rfq={selectedRfq} onBack={handleGoBack} />
}

// ── RFQ Detail & Quotation Form Component ───────────────────────────────────
export const REJECTION_REASONS: { value: RfqRejectionReason; label: string }[] = [
  { value: "OUT_OF_STOCK", label: "Out of Stock" },
  { value: "CAPACITY_FULL", label: "Capacity Full" },
  { value: "CANNOT_MEET_SPECIFICATIONS", label: "Cannot Meet Specifications" },
  { value: "CANNOT_MEET_LEAD_TIME", label: "Cannot Meet Lead Time" },
  { value: "PRICE_UNFEASIBLE", label: "Price Unfeasible" },
  { value: "REJECTED_BY_ADMIN", label: "Rejected by Admin" },
  { value: "OTHER", label: "Other" },
]

function RfqDetailView({ rfq, onBack }: { rfq: any; onBack: () => void }) {
  const navigate = useNavigate()
  const { data: variants, isLoading: loadingVariants } = useGetProductVariants()
  const rfqStatus = (rfq.status || "").toUpperCase()
  const isExpired = rfqStatus === "OPEN" && rfq.validUntil && new Date(rfq.validUntil) < new Date()
  const resolvedStatus = isExpired ? "EXPIRED" : (rfqStatus === "CLOSE" ? "CLOSED" : rfqStatus || "OPEN")
  const isOpen = resolvedStatus === "OPEN"

  const hasProductTargets = (rfq.targets || []).some((t: any) => t.product || t.productVariant)
  const targets = (hasProductTargets || !(rfq.targets && rfq.targets.length > 0))
    ? (rfq.targets || [])
    : rfq.company
    ? [{ id: rfq.company.id || "company-target", company: rfq.company, companyId: rfq.company.id, quantity: rfq.targets[0]?.quantity }]
    : (rfq.targets || [])

  const [message, setMessage] = useState(rfq.queryMessage || "")

  useEffect(() => {
    setMessage(rfq.queryMessage || "")
  }, [rfq.queryMessage])

  const { data: profile } = useSellerProfile()
  const { data: companyAddresses = [], isLoading: loadingAddresses, refetch: refetchAddresses } = useCompanyAddresses(profile?.companyId)
  const [showInlineAddAddress, setShowInlineAddAddress] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const queryClient = useQueryClient()
  const { mutate: rejectRfq, isPending: isRejecting } = useRejectRfqAdminToSeller()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState<RfqRejectionReason | "">("")
  const [rejectNote, setRejectNote] = useState("")
  const [rejectValidationError, setRejectValidationError] = useState<string | null>(null)

  const handleCloseRejectModal = () => {
    setShowRejectModal(false)
    setRejectReason("")
    setRejectNote("")
    setRejectValidationError(null)
  }

  const handleConfirmReject = () => {
    if (!rejectReason) {
      setRejectValidationError("Please select a reason for rejection.")
      return
    }

    if (rejectReason === "OTHER" && !rejectNote.trim()) {
      setRejectValidationError("Please provide a note explaining your reason.")
      return
    }

    const payload: RejectRfqAdminToSellerPayload = {
      reason: rejectReason,
      ...(rejectReason === "OTHER" && rejectNote.trim()
        ? { rejectionNote: rejectNote.trim() }
        : {}),
    }

    rejectRfq(
      { id: rfq.id, payload },
      {
        onSuccess: () => {
          toast.success("RFQ Rejected Successfully")
          handleCloseRejectModal()
          queryClient.invalidateQueries()
        },
        onError: () => {
          // Handled globally by http.ts response interceptor
        },
      }
    )
  }

  const isRejectSubmitDisabled = isRejecting || !rejectReason || (rejectReason === "OTHER" && !rejectNote.trim())
  const dropdownRef = useRef<HTMLDivElement>(null)
  const currencyDropdownRef = useRef<HTMLDivElement>(null)

  const [rfqOriginLocation, setRfqOriginLocation] = useState<any>(null)
  const [showLocationError, setShowLocationError] = useState(false)

  // Auto select default address
  useEffect(() => {
    if (!rfqOriginLocation && Array.isArray(companyAddresses) && companyAddresses.length > 0) {
      const defaultAddr = companyAddresses.find((a: any) => a.isDefault)
      if (defaultAddr) {
        setRfqOriginLocation(defaultAddr)
      } else {
        setRfqOriginLocation(companyAddresses[0])
      }
    }
  }, [companyAddresses, rfqOriginLocation])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setIsCurrencyDropdownOpen(false)
        setCurrencySearch("")
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  const [commodity, setCommodity] = useState("")
  const [totalPieces, setTotalPieces] = useState("")
  const [weight, setWeight] = useState("")
  const [volumeWeight, setVolumeWeight] = useState("")

  const commodityError = getCommodityError(commodity)
  const totalPiecesError = getTotalPiecesError(totalPieces)
  const weightError = getWeightError(weight)
  const volumeWeightError = getVolumeWeightError(volumeWeight)
  const [isLogisticsOpen, setIsLogisticsOpen] = useState(false)
  const [openProductDropdownIndex, setOpenProductDropdownIndex] = useState<number | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const variantDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openTaxTypeDropdownIndex, setOpenTaxTypeDropdownIndex] = useState<number | null>(null)
  const [taxTypeDropdownPosition, setTaxTypeDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const taxTypeDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openUnitDropdownIndex, setOpenUnitDropdownIndex] = useState<number | null>(null)
  const [unitDropdownPosition, setUnitDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const unitDropdownMenuRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (openProductDropdownIndex !== null) {
        const isMenu = variantDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="variant-${openProductDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenProductDropdownIndex(null)
        }
      }
      if (openTaxTypeDropdownIndex !== null) {
        const isMenu = taxTypeDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="taxtype-${openTaxTypeDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenTaxTypeDropdownIndex(null)
        }
      }
      if (openUnitDropdownIndex !== null) {
        const isMenu = unitDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="unit-${openUnitDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenUnitDropdownIndex(null)
        }
      }
    }
    const handleScroll = (e: Event) => {
      if (
        variantDropdownMenuRef.current?.contains(e.target as Node) ||
        taxTypeDropdownMenuRef.current?.contains(e.target as Node) ||
        unitDropdownMenuRef.current?.contains(e.target as Node)
      ) {
        return
      }
      setOpenProductDropdownIndex(null)
      setOpenTaxTypeDropdownIndex(null)
      setOpenUnitDropdownIndex(null)
    }

    document.addEventListener("mousedown", handleOutsideClick)
    window.addEventListener("scroll", handleScroll, true)
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [openProductDropdownIndex, openTaxTypeDropdownIndex, openUnitDropdownIndex])

  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null)

  const [currencyCode, setCurrencyCode] = useState("INR")
  const [validUntil, setValidUntil] = useState("")
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false)
  const [currencySearch, setCurrencySearch] = useState("")
  const { data: countryCurrenciesData, isLoading: isLoadingCurrencies } = useCountryCurrencies()

  const currencyOptions = (countryCurrenciesData && countryCurrenciesData.length > 0)
    ? countryCurrenciesData.map((item) => ({
        id: item.id,
        code: item.code,
        country: item.country,
        name: `${item.code} - ${item.country}`,
      }))
    : [
        { id: 105, code: "INR", country: "INDIA", name: "INR - INDIA" },
        { id: 2, code: "USD", country: "UNITED STATES", name: "USD - UNITED STATES" },
        { id: 3, code: "EUR", country: "EUROPEAN UNION", name: "EUR - EUROPEAN UNION" },
        { id: 4, code: "GBP", country: "UNITED KINGDOM", name: "GBP - UNITED KINGDOM" },
        { id: 5, code: "AED", country: "UNITED ARAB EMIRATES", name: "AED - UNITED ARAB EMIRATES" },
        { id: 6, code: "SGD", country: "SINGAPORE", name: "SGD - SINGAPORE" },
      ]

  const filteredCurrencies = currencyOptions.filter((curr) => {
    if (!currencySearch.trim()) return true
    const q = currencySearch.toLowerCase().trim()
    return curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q)
  })
  const [charges, setCharges] = useState<ChargeItem[]>([{ ...DEFAULT_CHARGE }])
  const [formError, setFormError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!formError) return
    const timer = setTimeout(() => {
      setFormError(null)
    }, 5000)
    return () => clearTimeout(timer)
  }, [formError])

  const canAddCharge = charges.every(isChargeRowCompleteAndValid)

  const handleAddCharge = () => {
    if (charges.length > 1) {
      const isPreviousRowsValid = charges.slice(0, charges.length - 1).every(isChargeRowCompleteAndValid)
      const isLatestRowValid = isChargeRowCompleteAndValid(charges[charges.length - 1])

      if (!isPreviousRowsValid) {
        toast.error("Please complete the validation of the current charge before adding another.")
        return
      }
      if (!isLatestRowValid) {
        toast.error("Please first finish the latest added row before adding another.")
        return
      }
    } else if (!canAddCharge) {
      toast.error("Please complete the validation of the current charge before adding another.")
      return
    }
    setCharges([...charges, { ...DEFAULT_CHARGE }])
  }

  const handleRemoveCharge = (index: number) => {
    setCharges(charges.filter((_, i) => i !== index))
  }

  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>, currentVal?: any, isIntegerOnly?: boolean) => {
    if (
      e.key === "-" ||
      e.key === "Subtract" ||
      e.code === "Minus" ||
      e.code === "NumpadSubtract" ||
      e.key === "+" ||
      e.key === "e" ||
      e.key === "E"
    ) {
      e.preventDefault()
    }
    if (isIntegerOnly && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
    if (e.key === "." && currentVal !== undefined && String(currentVal).includes(".")) {
      e.preventDefault()
    }
  }

  const handleChargeChange = (index: number, field: keyof ChargeItem, value: any) => {
    let sanitized = value
    const currentUnit = field === "unit" ? value : charges[index]?.unit

    if (typeof sanitized === "string") {
      if (field === "quantity") {
        if (isIntegerUnit(currentUnit)) {
          sanitized = sanitized.split(".")[0].replace(/\D/g, "")
        } else {
          sanitized = sanitized.replace(/[^0-9.]/g, "")
          const parts = sanitized.split(".")
          if (parts.length > 2) {
            sanitized = parts[0] + "." + parts.slice(1).join("")
          }
        }
      } else if (["unitPrice", "discount", "tax"].includes(field as string)) {
        sanitized = sanitized.replace(/[^0-9.]/g, "")
        const parts = sanitized.split(".")
        if (parts.length > 2) {
          sanitized = parts[0] + "." + parts.slice(1).join("")
        }
      }
    }

    const updated = [...charges]
    const updatedCharge = { ...updated[index], [field]: sanitized }

    // If unit changed to Piece or Box, trim out any decimal values from existing quantity
    if (field === "unit" && isIntegerUnit(sanitized)) {
      const currentQty = String(updatedCharge.quantity ?? "")
      if (currentQty.includes(".")) {
        updatedCharge.quantity = currentQty.split(".")[0].replace(/\D/g, "")
      }
    }

    updated[index] = updatedCharge
    setCharges(updated)
  }

  const mutation = useCreateQuotation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const reportValidationError = (errMsg: string, scrollTargetId?: string) => {
      setFormError(errMsg)
      setTimeout(() => {
        if (scrollTargetId) {
          document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: "smooth", block: "center" })
        } else {
          document.getElementById("quotation-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 50)
    }

    if (!message.trim()) {
      reportValidationError("Quotation message is required.")
      return
    }

    if (!currencyCode.trim()) {
      reportValidationError("Currency code is required.")
      return
    }
    if (charges.length === 0) {
      reportValidationError("At least one charge is required.")
      return
    }

    for (let i = 0; i < charges.length; i++) {
      const charge = charges[i]
      if (!charge.description.trim()) {
        reportValidationError(`Description for charge #${i + 1} is required.`)
        return
      }
      if (charge.description.trim().length > 50) {
        reportValidationError(`Description for charge #${i + 1} cannot exceed 50 characters.`)
        return
      }
      if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice)) || Number(charge.unitPrice) <= 0) {
        reportValidationError(`Please enter a valid unit price greater than 0 for charge #${i + 1}.`)
        return
      }
      if (Number(charge.unitPrice) > 10_000_000) {
        reportValidationError(`Unit price for charge #${i + 1} cannot exceed 10,000,000.`)
        return
      }
      const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined
      if (hasQuantity) {
        if (isNaN(Number(charge.quantity)) || Number(charge.quantity) <= 0) {
          reportValidationError(`Please enter a valid quantity greater than 0 for charge #${i + 1}.`)
          return
        }
        if (isIntegerUnit(charge.unit) && String(charge.quantity).includes(".")) {
          reportValidationError(`Quantity for charge #${i + 1} must be a whole number for ${charge.unit}.`)
          return
        }
        if (String(charge.quantity).includes(".") && String(charge.quantity).split(".")[1]?.length > 2) {
          reportValidationError(`Quantity for charge #${i + 1} can have at most 2 decimal places.`)
          return
        }
        if (Number(charge.quantity) >= 100_000) {
          reportValidationError(`Quantity for charge #${i + 1} must be less than 100,000.`)
          return
        }
        if (!charge.unit || !charge.unit.trim()) {
          reportValidationError(`Please select a unit for charge #${i + 1}.`)
          return
        }
      }
      if (charge.discount !== "" && charge.discount !== 0 && (isNaN(Number(charge.discount)) || Number(charge.discount) < 0 || Number(charge.discount) >= 100)) {
        reportValidationError(`Discount for charge #${i + 1} must be between 0% and 99.99%.`)
        return
      }
      if (charge.tax !== "" && charge.tax !== 0 && (isNaN(Number(charge.tax)) || Number(charge.tax) < 0 || Number(charge.tax) >= 100)) {
        reportValidationError(`Tax for charge #${i + 1} must be between 0% and 99.99%.`)
        return
      }
    }

    if (commodityError || totalPiecesError || weightError || volumeWeightError) {
      const logErr = commodityError || totalPiecesError || weightError || volumeWeightError || "Invalid logistics details"
      if (!isLogisticsOpen) setIsLogisticsOpen(true)
      const targetId = commodityError
        ? "logistics-commodity"
        : totalPiecesError
        ? "logistics-total-pieces"
        : weightError
        ? "logistics-weight"
        : "logistics-volume-weight"
      reportValidationError(logErr, targetId)
      return
    }

    const payload: any = {
      rfqId: rfq.buyerRfqId,
      companyId: rfq.sellerCompanyId,
      adminToSellerRfqId: rfq.id,
      message: message.trim(),
      currencyCode: currencyCode.trim(),
      expiresAt: validUntil ? (validUntil.includes("T") ? validUntil : `${validUntil}T12:00:00.000Z`) : undefined,
      originCityId: rfqOriginLocation?.cityId || rfqOriginLocation?._id || undefined,
      commodity: commodity.trim() || undefined,
      weight: weight.trim() !== "" ? Number(weight) : undefined,
      volumeWeight: volumeWeight.trim() !== "" ? Number(volumeWeight) : undefined,
      totalPieces: totalPieces.trim() !== "" ? Number(totalPieces) : undefined,
      charges: charges.map((c) => ({
        description: c.description.trim(),
        unitPrice: Number(c.unitPrice),
        discount: Number(c.discount) || 0,
        quantity: c.quantity !== "" && c.quantity !== undefined ? Number(c.quantity) : null,
        measureUnit: c.unit?.trim() || undefined,
        tax: Number(c.tax) || 0,
        taxType: c.taxType,
        productVariantId: c.productVariantId || undefined,
      })),
    }

    try {
      await mutation.mutateAsync(payload)
      toast.success("Quotation submitted successfully")
      navigate({ to: "/quotations" })
    } catch (err: any) {
      setFormError(err?.message || "Failed to submit quotation. Please try again.")
    }
  }

  const validUntilDate = rfq.validUntil ? new Date(rfq.validUntil) : null
  const diffTime = validUntilDate ? validUntilDate.getTime() - new Date().getTime() : 0
  const diffDays = validUntilDate ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : 0
  const expirySubtext = validUntilDate
    ? diffDays > 0
      ? `Expiring in ${diffDays} day${diffDays !== 1 ? "s" : ""}`
      : "Expired"
    : "No expiry specified"

  const isQuoting = isOpen && !successMessage

  return (
    <div className={cn("w-full animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto", isQuoting && "pb-64 sm:pb-48 md:pb-16")}>
      <div className="group relative overflow-hidden bg-white border border-primary/20 hover:border-primary/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-primary to-primary-hover rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer p-1"
              title="Back to RFQs"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-sm text-slate-700 font-medium ">Back to RFQs</span>
          </div>

          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-0.5 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
              resolvedStatus === "OPEN"
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : (resolvedStatus === "PENDING" || resolvedStatus === "REQUEST_REVISION")
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : (resolvedStatus === "EXPIRED" || resolvedStatus === "CANCELLED" || resolvedStatus === "REJECTED")
                ? "bg-red-50 text-red-700 border-red-100"
                : resolvedStatus === "QUOTED"
                ? "bg-blue-50 text-blue-700 border-blue-100"
                : resolvedStatus === "FORWARDED"
                ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                : "bg-slate-50 text-slate-700 border-slate-200"
            }`}>
              {resolvedStatus === "REQUEST_REVISION" ? "REQUEST REVISION" : resolvedStatus}
            </span>
            {rfq.status === "OPEN" && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  className="p-1.5 text-slate-500 hover:text-primary hover:bg-primary/5 hover:border-primary/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                  title="Actions"
                >
                  <MoreVertical className="size-4" />
                </button>
                {showActionsDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowActionsDropdown(false)}
                    />
                    <div className="absolute right-0 mt-1 w-35 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false)
                          setShowRejectModal(true)
                        }}
                        disabled={isRejecting}
                        className="w-full text-left px-3 py-2 text-xs font-bold text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="size-3.5" />
                        Reject Request
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="h-px bg-primary/10 w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {targets.length > 0 && targets.some((t: any) => t.product || t.productVariant) && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Package className="size-3.5 text-primary" /> Sourcing Target Item(s)
                </h4>
                <div className="grid grid-cols-1 gap-2">
                  {targets.map((target: any) => (
                    <TargetItemView
                      key={target.id}
                      target={target}
                      onCompanyClick={(c) => setSelectedCompany(c)}
                      onProductClick={(p) => setSelectedProduct(p)}
                      onVariantClick={(v) => setSelectedVariant(v)}
                      onExternalSellerClick={setSelectedExternalSeller}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Buyer Name */}
            {(rfq.buyer?.user?.name || rfq.buyerToAdminRfq?.buyer?.user?.name || rfq.buyerToAdminRFQ?.buyer?.user?.name) && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Building2 className="size-3.5 text-primary" /> Buyer Name
                </h4>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/15 bg-slate-50/20 p-3 shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-slate-700 font-semibold tracking-tight font-sans">
                      {rfq.buyer?.user?.name || rfq.buyerToAdminRfq?.buyer?.user?.name || rfq.buyerToAdminRFQ?.buyer?.user?.name}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <FileText className="size-3.5 text-primary" /> Procurement Request
              </h4>
              <p className="text-sm text-slate-700 font-medium bg-primary/[0.03] p-3.5 rounded-xl border border-primary/15 hover:border-primary/25 transition-colors leading-relaxed italic">
                {rfq.queryMessage || "No Procurement Request provided"}
              </p>
            </div>

            {(rfq.country || rfq.estimatedTime || rfq.moq || rfq.budget || rfq.paymentTerms || rfq.address) && (
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Tag className="size-3.5 text-primary" /> Sourcing Specifications
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">
                  {/* Quantity */}
                  {rfq.quantity && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                        <Tag className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Quantity</p>
                        <p className="text-xs font-bold text-slate-700">{rfq.quantity + " " + rfq.measureUnit}</p>
                      </div>
                    </div>
                  )}

                  {/* Budget */}
                  {rfq.budget && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                        <Tag className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Target Budget</p>
                        <p className="text-xs font-bold text-slate-700">{rfq.currencyCode + " " + rfq.budget}</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Terms */}
                  {rfq.paymentTerms && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                        <FileText className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Payment Terms</p>
                        <p className="text-xs font-bold text-slate-700">{rfq.paymentTerms}</p>
                      </div>
                    </div>
                  )}

                  {/* Lead Time */}
                  {rfq.leadTime && (
                    <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                        <Calendar className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Lead Time</p>
                        <p className="text-xs font-bold text-slate-700">{formatDate(rfq.leadTime)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isOpen && (
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={() => setIsLogisticsOpen(!isLogisticsOpen)}
                  className="text-[10px] font-black uppercase tracking-wider text-primary flex items-center gap-1.5 hover:opacity-85 transition-opacity cursor-pointer focus:outline-none select-none"
                >
                  {isLogisticsOpen ? (
                    <Minus className="size-3.5 text-primary shrink-0" />
                  ) : (
                    <Plus className="size-3.5 text-primary shrink-0" />
                  )}
                  <span>Logistics & Shipment Details</span>
                </button>
                {isLogisticsOpen && (
                  <div className="bg-primary/[0.03] border border-primary/20 rounded-xl p-4.5 shadow-sm transition-all duration-200 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1" id="logistics-commodity">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Commodity</label>
                        <input
                          type="text"
                          value={commodity}
                          onChange={(e) => setCommodity(e.target.value)}
                          placeholder="e.g. Handicrafts"
                          className={cn(
                            "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                            commodityError
                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                              : "border-slate-200 focus:border-primary"
                          )}
                          disabled={mutation.isPending}
                        />
                        {commodityError && (
                          <p className="text-[11px] text-primary font-semibold mt-1">
                            {commodityError}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1" id="logistics-total-pieces">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Quantity</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={totalPieces}
                          onChange={(e) => {
                            const clean = e.target.value.replace(/\D/g, "")
                            setTotalPieces(clean)
                          }}
                          onKeyDown={(e) => {
                            blockNegativeKeys(e)
                            if (e.key === ".") e.preventDefault()
                          }}
                          placeholder="e.g. 21"
                          className={cn(
                            "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                            totalPiecesError
                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                              : "border-slate-200 focus:border-primary"
                          )}
                          disabled={mutation.isPending}
                        />
                        {totalPiecesError && (
                          <p className="text-[11px] text-primary font-semibold mt-1">
                            {totalPiecesError}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1" id="logistics-weight">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Weight (kg)</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={weight}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === "" || /^\d*\.?\d*$/.test(val)) {
                              setWeight(val)
                            }
                          }}
                          onKeyDown={blockNegativeKeys}
                          placeholder="e.g. 100"
                          className={cn(
                            "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                            weightError
                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                              : "border-slate-200 focus:border-primary"
                          )}
                          disabled={mutation.isPending}
                        />
                        {weightError && (
                          <p className="text-[11px] text-primary font-semibold mt-1">
                            {weightError}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1" id="logistics-volume-weight">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Volume Weight (kg)</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={volumeWeight}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === "" || /^\d*\.?\d*$/.test(val)) {
                              setVolumeWeight(val)
                            }
                          }}
                          onKeyDown={blockNegativeKeys}
                          placeholder="e.g. 130"
                          className={cn(
                            "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                            volumeWeightError
                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                              : "border-slate-200 focus:border-primary"
                          )}
                          disabled={mutation.isPending}
                        />
                        {volumeWeightError && (
                          <p className="text-[11px] text-primary font-semibold mt-1">
                            {volumeWeightError}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="relative space-y-1 pb-2">
                      <div className="flex items-center justify-between">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                          Origin Address
                        </label>
                        {!showInlineAddAddress && (
                          <button
                            type="button"
                            onClick={() => setShowInlineAddAddress(true)}
                            className="text-[9.5px] font-bold text-primary hover:underline cursor-pointer flex items-center gap-1"
                          >
                            <Plus className="size-3" /> Add Address
                          </button>
                        )}
                      </div>

                      {showInlineAddAddress ? (
                        <div className="border border-primary/15 rounded-xl p-4 bg-primary/[0.02] mt-1">
                          <AddAddressForm
                            companyId={profile?.companyId}
                            onSuccess={(newAddr) => {
                              refetchAddresses()
                              setShowInlineAddAddress(false)
                              if (newAddr) {
                                setRfqOriginLocation(newAddr)
                              }
                            }}
                            onCancel={() => setShowInlineAddAddress(false)}
                            isInline={false}
                          />
                        </div>
                      ) : loadingAddresses ? (
                        <div className="flex items-center gap-2 py-2 px-3 border border-slate-100 rounded-lg text-slate-400 bg-white">
                          <Loader2 className="size-3.5 animate-spin text-primary" />
                          <span className="text-xs">Loading addresses...</span>
                        </div>
                      ) : companyAddresses.length > 0 ? (
                        <div ref={dropdownRef} className="relative">
                          <button
                            type="button"
                            onClick={() => setIsDropdownOpen((p) => !p)}
                            className={`w-full flex items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200 cursor-pointer ${
                              showLocationError && !rfqOriginLocation
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-slate-200 focus:border-primary"
                            }`}
                          >
                            {rfqOriginLocation ? (
                              <div className="flex items-center gap-1.5 truncate">
                                <MapPin className="size-3.5 text-primary shrink-0" />
                                <span className="font-bold text-slate-800 truncate">
                                  {rfqOriginLocation.title}:
                                </span>
                                <span className="text-slate-500 truncate">
                                  {[
                                    rfqOriginLocation.addressLine1,
                                    rfqOriginLocation.city,
                                    rfqOriginLocation.state,
                                    rfqOriginLocation.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                                {rfqOriginLocation.isDefault && (
                                  <BadgeCheck className="size-3.5 text-primary shrink-0" />
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">Select origin address...</span>
                            )}
                            <ChevronDown className={`size-4 text-slate-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                          </button>

                          {isDropdownOpen && (
                            <ul className="absolute z-50 w-full mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg overscroll-contain">
                              {companyAddresses.map((addr: any) => (
                                <li
                                  key={addr.id}
                                  onClick={() => {
                                    setRfqOriginLocation(addr)
                                    setIsDropdownOpen(false)
                                    setShowLocationError(false)
                                  }}
                                  className={`px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between gap-2 ${
                                    rfqOriginLocation?.id === addr.id ? "bg-slate-50 text-primary font-bold" : ""
                                  }`}
                                >
                                  <div className="truncate text-left">
                                    <div className="font-bold text-slate-800 text-xs">
                                      {addr.title}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                      {[
                                        addr.addressLine1,
                                        addr.city,
                                        addr.state,
                                        addr.postalCode,
                                        addr.country,
                                      ]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </div>
                                  </div>
                                  {addr.isDefault && (
                                    <BadgeCheck className="size-4 text-primary shrink-0" />
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowInlineAddAddress(true)}
                            className="w-full flex items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-xs text-slate-800 focus:outline-none transition-all duration-200 cursor-pointer border-slate-200"
                          >
                            <div className="flex items-center gap-1.5 truncate">
                              <MapPin className="size-3.5 text-slate-400 shrink-0" />
                              <span className="text-slate-400">No Address Available</span>
                            </div>
                            <span className="text-[11px] font-bold text-primary hover:underline">
                              Add Address
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isOpen && (
              <div className="space-y-3">
                <div className="flex items-center gap-1">
                  <Plus className="size-3.5 text-primary" />
                  <h4 className="text-[10px] font-black uppercase tracking-wider text-primary">
                    Submit Quotation
                  </h4>
                </div>

                <div className="bg-primary/[0.03] border border-primary/20 rounded-xl p-4.5 space-y-4 shadow-sm transition-all duration-200">
                  {successMessage ? (
                    <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800 font-semibold animate-in fade-in duration-200">
                      <Check className="size-4 text-emerald-600 shrink-0" />
                      <span>{successMessage}</span>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                          Message / Terms
                        </label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="e.g., Delivery within 5 business days."
                          className="w-full min-h-[70px] max-h-[140px] rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:border-primary focus:outline-none transition-all duration-200"
                          disabled={mutation.isPending}
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:flex-nowrap gap-4 sm:gap-8">
                          <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">
                              Currency Code
                            </label>
                            <div className="relative w-full sm:w-[80px]" ref={currencyDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                                disabled={mutation.isPending}
                                className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-primary focus:outline-none transition-all duration-200 cursor-pointer text-left"
                              >
                                <span>{currencyCode}</span>
                                <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isCurrencyDropdownOpen ? "rotate-180" : ""}`} />
                              </button>

                              {isCurrencyDropdownOpen && (
                                <div
                                  className="absolute left-0 z-50 mt-1 w-56 sm:w-64 bg-white border border-slate-200 rounded-lg shadow-lg text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col"
                                >
                                  {/* Search Header */}
                                  <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                                    <input
                                      type="text"
                                      value={currencySearch}
                                      onChange={(e) => setCurrencySearch(e.target.value)}
                                      placeholder="Search currency or country..."
                                      className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-primary text-slate-700 placeholder:text-slate-400"
                                      onClick={(e) => e.stopPropagation()}
                                      autoFocus
                                    />
                                  </div>

                                  {/* Scrollable list */}
                                  <div className="max-h-[160px] overflow-y-auto py-1">
                                    {isLoadingCurrencies && (
                                      <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                                        <Loader2 className="size-3 animate-spin text-primary" />
                                        <span>Loading currencies...</span>
                                      </div>
                                    )}
                                    {filteredCurrencies.map((curr, idx) => (
                                      <button
                                        key={(curr as any).id ?? `${curr.code}-${idx}`}
                                        type="button"
                                        onClick={() => {
                                          setCurrencyCode(curr.code)
                                          setIsCurrencyDropdownOpen(false)
                                          setCurrencySearch("")
                                        }}
                                        title={curr.name}
                                        className={cn(
                                          "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate block",
                                          currencyCode === curr.code ? "text-primary bg-primary/10 font-bold" : ""
                                        )}
                                      >
                                        {curr.name}
                                      </button>
                                    ))}
                                    {!isLoadingCurrencies && filteredCurrencies.length === 0 && (
                                      <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                                        No currency found
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">
                              Valid Until
                            </label>
                            <div className="w-full sm:w-[170px]">
                              <DatePicker
                                value={validUntil}
                                onChange={setValidUntil}
                                min={new Date().toISOString().split("T")[0]}
                                placeholder="Select valid date"
                                disabled={mutation.isPending}
                                className="!h-[34px] !py-1.5 !text-xs !bg-white !rounded-lg"
                              />
                            </div>
                          </div>
                        </div>

                        <div id="quotation-charges-section" className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                              Charges / Fees
                            </label>
                            <button
                              type="button"
                              onClick={handleAddCharge}
                              disabled={mutation.isPending}
                              className="flex items-center gap-1 text-[10px] font-bold text-primary hover:text-primary-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="size-3" /> Add Charge
                            </button>
                          </div>

                          {formError && (
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/5 p-2.5 rounded-lg border border-primary/20 animate-in fade-in duration-200">
                              <AlertCircle className="size-4 shrink-0 text-primary" />
                              <span>{formError}</span>
                            </div>
                          )}

                          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mt-2">
                            <table className="w-full text-left text-xs whitespace-nowrap">
                              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                <tr>
                                  <th className="p-3 w-56">Product (Optional)</th>
                                  <th className="p-3">Description</th>
                                  <th className="p-3 w-32">Unit Price</th>
                                  <th className="p-3 w-24">Quantity</th>
                                  <th className="p-3 w-28">Unit</th>
                                  <th className="p-3 w-28">Discount (%)</th>
                                  <th className="p-3 w-24">Tax (%)</th>
                                  <th className="p-3 w-24">Tax Type</th>
                                  <th className="p-3 text-right">Total</th>
                                  <th className="p-3 w-10"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200 bg-white">
                                {charges.map((charge, index) => {
                                  const qty = charge.quantity !== "" && charge.quantity !== undefined ? (Number(charge.quantity) || 0) : 1
                                  const base = (Number(charge.unitPrice) || 0) * qty
                                  const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                                  const total = subtotal + (subtotal * ((Number(charge.tax) || 0) / 100))

                                  const descError = getChargeDescriptionError(charge.description)
                                  const unitPriceError = getChargeUnitPriceError(charge.unitPrice)
                                  const quantityError = getChargeQuantityError(charge.quantity, charge.unit)
                                  const unitError = getChargeUnitError(charge.unit, charge.quantity)
                                  const discountError = getChargeDiscountError(charge.discount)
                                  const taxError = getChargeTaxError(charge.tax)

                                  const isRowDisabled = index > 0 && !charges.slice(0, index).every(isChargeRowCompleteAndValid)

                                  return (
                                    <tr key={index} className={cn("hover:bg-slate-50/50 transition-colors align-top", isRowDisabled && "bg-slate-50/40 opacity-70")}>
                                      <td className="p-2 min-w-[200px]">
                                        <button
                                          type="button"
                                          data-dropdown-trigger={`variant-${index}`}
                                          onClick={(e) => {
                                            if (isRowDisabled) return
                                            const rect = e.currentTarget.getBoundingClientRect()
                                            setDropdownPosition({
                                              top: rect.bottom,
                                              left: rect.left,
                                              width: rect.width,
                                            })
                                            setOpenProductDropdownIndex(prev => prev === index ? null : index)
                                          }}
                                          className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-primary focus:outline-none transition-all duration-200 h-[34px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                          disabled={mutation.isPending || loadingVariants || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        >
                                          <span className="truncate">
                                            {charge.productVariantId && Array.isArray(variants)
                                              ? variants.find((v: any) => v.id === charge.productVariantId)?.name || "Select Variant"
                                              : "Select Variant"
                                            }
                                          </span>
                                          <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openProductDropdownIndex === index && "rotate-180")} />
                                        </button>

                                        {openProductDropdownIndex === index && dropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                          <ul
                                            ref={variantDropdownMenuRef}
                                            style={{
                                              position: "fixed",
                                              top: `${dropdownPosition.top + 4}px`,
                                              left: `${dropdownPosition.left}px`,
                                              width: `${dropdownPosition.width}px`,
                                            }}
                                            className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                          >
                                            <li
                                              onClick={() => {
                                                const updated = [...charges]
                                                const targetCharge = { ...updated[index], productVariantId: "", unitPrice: "" as const, description: "" } as ChargeItem
                                                updated[index] = targetCharge
                                                setCharges(updated)
                                                setOpenProductDropdownIndex(null)
                                              }}
                                              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 cursor-pointer transition-colors border-b border-slate-50 last:border-0"
                                            >
                                              Select Variant
                                            </li>
                                            {Array.isArray(variants) && variants.map((v: any) => (
                                              <li
                                                key={v.id}
                                                onClick={() => {
                                                  const updated = [...charges]
                                                  const targetCharge = { ...updated[index], productVariantId: v.id }
                                                  if (v.price) targetCharge.unitPrice = Number(v.price)
                                                  if (v.name) targetCharge.description = v.name
                                                  updated[index] = targetCharge
                                                  setCharges(updated)
                                                  setOpenProductDropdownIndex(null)
                                                }}
                                                className={cn(
                                                  "px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                                  charge.productVariantId === v.id && "bg-primary/10 text-primary font-bold"
                                                )}
                                              >
                                                {v.name}
                                              </li>
                                            ))}
                                          </ul>,
                                          document.body
                                        )}
                                      </td>
                                      <td className="p-2 min-w-[200px]">
                                        <input
                                          type="text"
                                          value={charge.description}
                                          onChange={(e) => handleChargeChange(index, "description", e.target.value)}
                                          placeholder="e.g. Shipping Fee, Customs"
                                          className={cn(
                                            "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                            descError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          required
                                        />
                                        {descError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1">
                                            {descError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-32">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          value={charge.unitPrice}
                                          onChange={(e) => handleChargeChange(index, "unitPrice", e.target.value)}
                                          onKeyDown={(e) => blockNegativeKeys(e, charge.unitPrice)}
                                          placeholder="0.00"
                                          className={cn(
                                            "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                            unitPriceError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          required
                                        />
                                        {unitPriceError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                            {unitPriceError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-28">
                                        <input
                                          type="text"
                                          inputMode={isIntegerUnit(charge.unit) ? "numeric" : "decimal"}
                                          value={charge.quantity}
                                          onChange={(e) => handleChargeChange(index, "quantity", e.target.value)}
                                          onKeyDown={(e) => blockNegativeKeys(e, charge.quantity, isIntegerUnit(charge.unit))}
                                          placeholder="0"
                                          className={cn(
                                            "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                            quantityError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        />
                                        {quantityError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                            {quantityError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-28">
                                        <button
                                          type="button"
                                          data-dropdown-trigger={`unit-${index}`}
                                          onClick={(e) => {
                                            if (isRowDisabled) return
                                            const rect = e.currentTarget.getBoundingClientRect()
                                            setUnitDropdownPosition({
                                              top: rect.bottom,
                                              left: rect.left,
                                              width: rect.width,
                                            })
                                            setOpenUnitDropdownIndex(prev => prev === index ? null : index)
                                          }}
                                          className={cn(
                                            "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                            unitError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        >
                                          <span className={cn("truncate", !charge.unit && "text-slate-400 font-normal")}>
                                            {charge.unit
                                              ? (QUANTITY_UNITS.find((u) => u.code === charge.unit)?.name || charge.unit)
                                              : "Select"}
                                          </span>
                                          <ChevronDown
                                            className={cn(
                                              "size-3.5 text-slate-400 transition-transform shrink-0 ml-1",
                                              openUnitDropdownIndex === index && "rotate-180"
                                            )}
                                          />
                                        </button>

                                        {openUnitDropdownIndex === index && unitDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                          <ul
                                            ref={unitDropdownMenuRef}
                                            style={{
                                              position: "fixed",
                                              top: `${unitDropdownPosition.top + 4}px`,
                                              left: `${unitDropdownPosition.left}px`,
                                              width: `${Math.max(unitDropdownPosition.width, 150)}px`,
                                            }}
                                            className="z-[99999] max-h-[160px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                          >
                                            <li
                                              onClick={() => {
                                                handleChargeChange(index, "unit", "")
                                                setOpenUnitDropdownIndex(null)
                                              }}
                                              className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50"
                                            >
                                              Select Unit
                                            </li>
                                            {QUANTITY_UNITS.map((u) => (
                                              <li
                                                key={u.code}
                                                onClick={() => {
                                                  handleChargeChange(index, "unit", u.code)
                                                  setOpenUnitDropdownIndex(null)
                                                }}
                                                className={cn(
                                                  "px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                                  charge.unit === u.code && "bg-primary/10 text-primary font-bold"
                                                )}
                                                title={u.name}
                                              >
                                                {u.name}
                                              </li>
                                            ))}
                                          </ul>,
                                          document.body
                                        )}
                                        {unitError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                            {unitError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-28">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          value={charge.discount}
                                          onChange={(e) => handleChargeChange(index, "discount", e.target.value)}
                                          onKeyDown={(e) => blockNegativeKeys(e, charge.discount)}
                                          placeholder="0"
                                          className={cn(
                                            "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                            discountError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        />
                                        {discountError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                            {discountError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-24">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          value={charge.tax}
                                          onChange={(e) => handleChargeChange(index, "tax", e.target.value)}
                                          onKeyDown={(e) => blockNegativeKeys(e, charge.tax)}
                                          placeholder="18"
                                          className={cn(
                                            "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                            taxError
                                              ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                              : "border-slate-200 focus:border-primary",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        />
                                        {taxError && !isRowDisabled && (
                                          <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                            {taxError}
                                          </p>
                                        )}
                                      </td>
                                      <td className="p-2 w-24">
                                        <button
                                          type="button"
                                          data-dropdown-trigger={`taxtype-${index}`}
                                          onClick={(e) => {
                                            if (isRowDisabled) return
                                            const rect = e.currentTarget.getBoundingClientRect()
                                            setTaxTypeDropdownPosition({
                                              top: rect.bottom,
                                              left: rect.left,
                                              width: rect.width,
                                            })
                                            setOpenTaxTypeDropdownIndex(prev => prev === index ? null : index)
                                          }}
                                          className={cn(
                                            "w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-primary focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                            isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                          )}
                                          disabled={mutation.isPending || isRowDisabled}
                                          title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                        >
                                          <span className="truncate">
                                            {charge.taxType === "NONE" ? "None" : (charge.taxType || "GST")}
                                          </span>
                                          <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openTaxTypeDropdownIndex === index && "rotate-180")} />
                                        </button>

                                        {openTaxTypeDropdownIndex === index && taxTypeDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                          <ul
                                            ref={taxTypeDropdownMenuRef}
                                            style={{
                                              position: "fixed",
                                              top: `${taxTypeDropdownPosition.top + 4}px`,
                                              left: `${taxTypeDropdownPosition.left}px`,
                                              width: `${taxTypeDropdownPosition.width}px`,
                                            }}
                                            className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                          >
                                            {[
                                              { value: "GST", label: "GST" },
                                              { value: "VAT", label: "VAT" },
                                              { value: "NONE", label: "None" },
                                            ].map((option) => (
                                              <li
                                                key={option.value}
                                                onClick={() => {
                                                  handleChargeChange(index, "taxType", option.value)
                                                  setOpenTaxTypeDropdownIndex(null)
                                                }}
                                                className={cn(
                                                  "px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                                  charge.taxType === option.value && "bg-primary/10 text-primary font-bold"
                                                )}
                                              >
                                                {option.label}
                                              </li>
                                            ))}
                                          </ul>,
                                          document.body
                                        )}
                                      </td>
                                      <td className="p-2 text-center font-black text-slate-800 pt-3.5">
                                        {Math.max(0, total).toFixed(2)}
                                      </td>
                                      <td className="p-2 text-center pt-2.5">
                                        {charges.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveCharge(index)}
                                            disabled={mutation.isPending}
                                            className="p-1.5 rounded-md border border-destructive/20 bg-destructive/10 hover:bg-destructive/15 text-destructive transition-all cursor-pointer shadow-sm"
                                            title="Remove charge"
                                          >
                                            <X className="size-3.5" />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                            <span className="text-[12px] font-bold text-primary">
                              {currencyCode} {charges.reduce((acc, charge) => {
                                const qty = charge.quantity !== "" && charge.quantity !== undefined ? (Number(charge.quantity) || 0) : 1
                                const base = (Number(charge.unitPrice) || 0) * qty
                                const subtotal = base - (base * (Number(charge.discount) / 100))
                                return acc + Math.max(0, subtotal + (subtotal * (Number(charge.tax) / 100)))
                              }, 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2.5 justify-end pt-1">
                        <button
                          type="button"
                          onClick={onBack}
                          disabled={mutation.isPending}
                          className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={mutation.isPending}
                          className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-primary-foreground text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1.5 transition-colors shadow-xs disabled:opacity-50"
                        >
                          {mutation.isPending ? (
                            <>
                              <Loader2 className="size-3 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>Submit Quotation</>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-primary/10 lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-primary" /> Timestamps
              </h4>

              <div className="relative pl-5 space-y-4">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-primary/20"></div>

                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                    <div className="size-1.5 rounded-full bg-sky-600"></div>
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">RECEIVED DATE</span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(rfq.createdAt)}</p>
                  </div>
                </div>

                {rfq.validUntil && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-red-100 bg-[#fee2e2]">
                      <div className="size-1.5 rounded-full bg-red-600"></div>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">VALID UNTIL</span>
                      <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(rfq.validUntil)}</p>
                      <p className={`text-[9px] font-semibold ${diffDays > 0 ? "text-primary" : "text-slate-400"}`}>
                        {expirySubtext}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Empty space on mobile only when quotation form is open so user can scroll above keyboard */}
      {isQuoting && (
        <div className="h-16 sm:h-8 md:hidden pointer-events-none" aria-hidden="true" />
      )}

      {selectedVariant && (
        <VariantDetailSidebar
          variant={selectedVariant}
          onClose={() => setSelectedVariant(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={false}
        />
      )}
      {selectedProduct && (
        <ProductDetailSidebar
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={false}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          level={selectedProduct || selectedVariant ? 1 : 0}
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar
          externalSeller={selectedExternalSeller}
          onClose={() => setSelectedExternalSeller(null)}
        />
      )}

      {showRejectModal && (
        <PromptModalCard
          title="Reject RFQ Request?"
          description="Please select a reason for rejecting this RFQ. The admin will be notified."
          icon={AlertTriangle}
          iconContainerClassName="bg-red-50 text-red-600"
          iconClassName="size-6"
          primaryAction={{
            label: "Keep RFQ",
            className: "flex-1 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer",
            onClick: handleCloseRejectModal
          }}
          secondaryAction={{
            label: isRejecting ? "Rejecting..." : "Reject RFQ",
            disabled: isRejectSubmitDisabled,
            className: "flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: handleConfirmReject
          }}
          onClose={handleCloseRejectModal}
          onCloseText=""
        >
          <div className="space-y-3 pt-1 text-left">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={rejectReason}
                  onChange={(e) => {
                    const val = e.target.value as RfqRejectionReason
                    setRejectReason(val)
                    if (val !== "OTHER") {
                      setRejectNote("")
                    }
                    setRejectValidationError(null)
                  }}
                  className="w-full text-xs font-medium bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition appearance-none cursor-pointer pr-8"
                >
                  <option value="" disabled>Select a reason...</option>
                  {REJECTION_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="size-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {rejectReason === "OTHER" && (
              <div className="space-y-1.5 animate-in fade-in duration-150">
                <label className="block text-xs font-semibold text-slate-700">
                  Please specify reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => {
                    setRejectNote(e.target.value)
                    if (rejectValidationError) setRejectValidationError(null)
                  }}
                  placeholder="Enter reason for rejection..."
                  rows={3}
                  className="w-full text-xs font-normal bg-white border border-slate-200 rounded-xl p-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition resize-none"
                />
              </div>
            )}

            {rejectValidationError && (
              <p className="text-xs text-red-500 font-medium">
                {rejectValidationError}
              </p>
            )}
          </div>
        </PromptModalCard>
      )}
    </div>
  )
}
